import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';

import BTE from 'lib/BTE';
import Breakpoints from 'lib/Breakpoints';
import { ECOMMERCE_DISCLAIMER } from 'lib/commerceUtils';

import { DisclaimerModal } from 'components/Disclaimer';
import { CollectionLead } from 'components/packages/CollectionLead';
import { Filter } from 'components/packages/Waffle/Filter';
import { VerticalContext } from 'lib/ContextTypes';

import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { PRODUCT_WAFFLE_DISCLAIMER_LINK } from 'lib/brandFeatures';
import styles from './styles.module.scss';

$t('register', 'waffle_filters');

/**
 * Returns the appropriate disclaimer text based on the provided key and override.
 * @param {string} vertical - The vertical context ('today', etc.) to determine branding.
 * @param {string} key - The type of disclaimer to return ('DEFAULT', 'PRODUCT', 'NONE', 'OVERRIDE', or a key in ECOMMERCE_DISCLAIMER).
 * @param {string} [override] - Optional override text to use if key is 'OVERRIDE'.
 * @returns {string|null} The disclaimer text, or null if key is 'NONE'.
 */

/**
 * Locks scroll position
 */
function lockBodyScroll() {
  const container = document.getElementById('scroll-container');
  if (!container || container.dataset.locked === '1') return;

  const y = window.scrollY || 0;
  container.dataset.locked = '1';
  container.dataset.scrollY = String(y);

  container.classList.add('verticalScrollDisabled');
  container.style.top = `-${y}px`;
}


/**
 * Unlocks scroll position
 */
function unlockBodyScroll() {
  const container = document.getElementById('scroll-container');
  if (!container) return;

  const saved = parseInt(container.dataset.scrollY || '0', 10) || 0;

  container.classList.remove('verticalScrollDisabled');
  container.style.top = '';

  delete container.dataset.locked;
  delete container.dataset.scrollY;

  window.scrollTo(0, saved);
}


/**
 * Returns the appropriate disclaimer text based on the provided key and override.
 * @param {string} vertical - The vertical context (e.g., 'today') to determine branding.
 * @param {string} key - The type of disclaimer ('DEFAULT', 'PRODUCT', 'NONE', 'OVERRIDE', or a key in ECOMMERCE_DISCLAIMER).
 * @param {string} [override] - Optional override text used if key is 'OVERRIDE'.
 * @returns {string|null} The disclaimer text, or null if key is 'NONE'.
 */
function getDisclaimer(vertical, key, override) {
  const defaultDisclaimer = `${vertical === 'today' ? 'Shop TODAY' : 'NBC Select'} independently determines what we cover and recommend.`
    + ' When you buy through our links, we earn a commission.'
    + ` <a href=${getFeatureConfigForBrand(PRODUCT_WAFFLE_DISCLAIMER_LINK, vertical)} target="_blank">Learn more.</a>`;

  const hasOverride = key === 'OVERRIDE'
    && typeof override === 'string'
    && override?.length > 0;

  if (hasOverride) return override;
  if (key === 'NONE') return null;
  if (key === 'DEFAULT') {
    return defaultDisclaimer;
  }
  if (key === 'PRODUCT') {
    return 'We may earn a commission when you purchase through our links.';
  }

  return ECOMMERCE_DISCLAIMER[key] || defaultDisclaimer;
}

/**
 * HeaderDek component displays the description, short description, and disclaimer for the Waffle header.
 * @param {object} props - Component props.
 * @param {string} props.description - The long description text.
 * @param {string} props.shortDescription - The short description text.
 * @param {string} props.disclaimerKey - Key to determine which disclaimer to show.
 * @param {string} props.disclaimerOverride - Optional override text for the disclaimer.
 * @param {string} props.subType - The subtype of the header (e.g., 'autofilledProductWaffle').
 * @param {string} props.type - The type of the header (e.g., 'waffle').
 * @param {boolean} props.showDisclaimer - Whether to show the disclaimer.
 * @returns {JSX.Element|null} The rendered header dek or null if nothing to display.
 */
const HeaderDek = ({
  description,
  shortDescription,
  disclaimerKey,
  disclaimerOverride,
  subType,
  type,
  showDisclaimer,
}) => {
  const vertical = useContext(VerticalContext);

  if (!description && !shortDescription && !showDisclaimer) {
    return null;
  }

  const isProductWaffle = type === 'waffle'
    && ['autofilledProductWaffle', 'giftGuideWaffle'].includes(subType);
  const shouldShowDisclaimerModal = showDisclaimer && !isProductWaffle;
  const shouldShowDisclaimerText = showDisclaimer && isProductWaffle;

  const disclaimerHTML = getDisclaimer(vertical, disclaimerKey, disclaimerOverride);

  return (
    <div className={styles.dek}>
      {(description || shortDescription) && (
        <p className={styles.description}>
          {shortDescription && (
            <span className={styles.short}>{shortDescription}</span>
          )}
          {description && (
            <span className={styles.long}>{description}</span>
          )}
        </p>
      )}
      {shouldShowDisclaimerModal && (
        <div className={styles.disclaimerModal}>
          <DisclaimerModal
            disclaimerKey={disclaimerKey}
            disclaimerOverride={disclaimerOverride}
          />
        </div>
      )}
      {shouldShowDisclaimerText && (
        <div
          className={styles.disclaimerText}
          data-testid="waffle-header__disclaimer"
          dangerouslySetInnerHTML={{ __html: disclaimerHTML }}
        />
      )}
    </div>
  );
};

/**
 * FilterTagList component displays active filter tags and a clear button.
 * @param {object} props - Component props.
 * @param {Array} props.activeFilters - List of currently active filters.
 * @param {Function} props.clearFilter - Callback to clear all filters.
 * @param {Function} props.filterOnChange - Callback to remove a specific filter.
 * @param {boolean} props.enableMultiselectFilters - Whether multiselect is enabled for filters.
 * @param {object} props.categoryToDisplay - Mapping of filter categories to display names.
 * @returns {JSX.Element} The rendered filter tag list.
 */
const FilterTagList = ({
  activeFilters,
  clearFilter,
  filterOnChange,
  enableMultiselectFilters,
  categoryToDisplay,
}) => {
  const clearText = enableMultiselectFilters
    ? i18next.t('Clear All')
    : i18next.t('Clear Filter');

  /**
   * Add tracking when a user clears a filter with the x button
   * @param {string} category the category of the filter that's being cleared
   */

  const trackFilterOnChange = (category) => {
    $t('track', 'waffle_filters', {
      action: 'remove filter',
      filter: category,
    });
  };

  const tagList = activeFilters.map(({ key, category, displayName }) => (
    <button
      data-testid="waffleHeader__activeFilter"
      data-filter-action="remove"
      className={styles.tagListBtn}
      key={key}
      onClick={() => {
        filterOnChange(category)(key, false);
        trackFilterOnChange(categoryToDisplay[category]);
      }}
      type="button"
    >
      <span className={classNames(styles.iconDelete, 'icon icon-close')} />
      {displayName}
    </button>
  ));

  return (
    <>
      <div className={styles.tagList}>
        {tagList}
      </div>
      <button
        className={styles.clearAllBtn}
        data-testid="waffleHeader__clear-all"
        data-filter-action="clear"
        onClick={() => {
          clearFilter();
        }}
        type="button"
      >
        {clearText}
      </button>
      <span className={styles.resultsSeparator}>|</span>
    </>
  );
};

/**
 * Filters component for the Waffle header.
 * Renders filter accordions, overlay, and clear/apply buttons for mobile filter UI.
 *
 * @param {object} props - Component props.
 * @param {Array} props.activeFilters - Currently active filters.
 * @param {string} props.activeFilterKey - Key of the currently active filter accordion.
 * @param {object} props.categories - Filter categories and their options.
 * @param {string} props.categoryToDisplay - Category key to display.
 * @param {Function} props.clearActiveAccordion - Callback to clear the active accordion.
 * @param {Function} props.clearFilter - Callback to clear all filters.
 * @param {Function} props.closeFilterOverlay - Callback to close the filter overlay.
 * @param {object} props.content - Content metadata, including description and shortDescription.
 * @param {boolean} props.enableMultiselectFilters - Whether multiselect is enabled for filters.
 * @param {Function} props.filterDisplayNameByKey - Function to get display name by filter key.
 * @param {Function} props.filterOnChange - Callback when a filter changes.
 * @param {boolean} props.filterOverlayOpen - Whether the filter overlay is open.
 * @param {object} props.filterValues - Current filter values.
 * @param {boolean} props.isSorM - Whether the view is small or medium (mobile).
 * @param {Function} props.onFilterClose - Callback when filter overlay closes.
 * @param {Function} props.setDesktopActiveAccordion - Callback to set the active accordion on desktop.
 * @returns {JSX.Element} The rendered filters component.
 */
const Filters = ({
  activeFilterKey,
  activeFilters = [],
  categories,
  categoryToDisplay,
  clearActiveAccordion,
  clearFilter,
  closeFilterOverlay,
  content,
  enableMultiselectFilters,
  filterDisplayNameByKey,
  filterOnChange,
  filterOverlayOpen,
  filterValues,
  isSorM,
  onFilterClose,
  setDesktopActiveAccordion,
}) => {
  const {
    metadata: {
      description,
      shortDescription,
    },
  } = content;

  const clearText = enableMultiselectFilters
    ? i18next.t('CLEAR ALL')
    : i18next.t('CLEAR');
  const categoryEntries = Object.entries(categories);
  const onlyOneFilter = categoryEntries.length === 1;

  return (
    <>
      <div
        className={classNames(
          styles.tester,
          { [styles.open]: filterOverlayOpen },
        )}
      />
      <div
        className={classNames(
          styles.overlayBg,
          { [styles.open]: filterOverlayOpen },
        )}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
        }}
        aria-hidden="true"
        onClick={() => {
          closeFilterOverlay();
          clearActiveAccordion();
        }}
        data-testid="waffle-header__overlay-bg"
      />
      <div
        className={classNames(
          styles.filters,
          {
            [styles.open]: filterOverlayOpen,
            [styles.afterDek]: (shortDescription || description),
          },
        )}
      >
        {categoryEntries.map(([category, values]) => (
          <Filter
            mobileFiltersEnabled
            onlyOneFilter={onlyOneFilter}
            categoryName={category}
            categoryToDisplay={categoryToDisplay}
            categoryValues={values}
            enableMultiselectFilters={enableMultiselectFilters}
            filterDisplayNameByKey={filterDisplayNameByKey}
            key={category}
            onChange={filterOnChange(category)}
            value={filterValues[category] || {}}
            activeFilterKey={activeFilterKey}
            isOpen={activeFilterKey === category}
            isSorM={isSorM}
            setDesktopActiveAccordion={(key) => {
              setDesktopActiveAccordion(key);
            }}
            clearActiveAccordion={clearActiveAccordion}
            onFilterClose={onFilterClose}
          />
        ))}
        <div className={styles.overlayBtnWrapper}>
          <button
            type="button"
            disabled={!activeFilters.length}
            className={classNames(
              styles.overlayBtn,
              styles.secondary,
              { [styles.disabled]: !activeFilters.length },
            )}
            data-testid="waffleHeader__filter-clear-all"
            onClick={() => {
              clearFilter();
            }}
            aria-label="Clear all filter selections"
          >
            {clearText}
          </button>
          <button
            className={classNames(styles.overlayBtn, styles.primary, styles.doneBtn)}
            onClick={closeFilterOverlay}
            type="button"
            aria-label="Apply filters "
          >
            {i18next.t('DONE')}
          </button>
        </div>
      </div>
    </>
  );
};

/**
 * Header component for the Waffle package.
 * Renders the collection lead, filters, results count, and handles filter overlay logic for both desktop and mobile.
 *
 * @param {object} props - Component props.
 * @param {object} props.activeFilters - Currently active filters.
 * @param {number} props.cardCount - Number of cards/results to display.
 * @param {object} props.categories - Filter categories and their options.
 * @param {string} props.categoryToDisplay - Category key to display.
 * @param {Function} props.clearFilter - Callback to clear all filters.
 * @param {object} [props.content={}] - Content metadata and filter display names/values.
 * @param {boolean} props.enableMultiselectFilters - Whether multiselect is enabled for filters.
 * @param {object} props.filterDisplayNameByKey - Mapping of filter keys to display names.
 * @param {() => () => void} props.filterOnChange - Callback when a filter changes.
 * @param {object} props.filterValues - Current filter values.
 * @param {boolean} props.isFilterActive - Whether any filter is currently active.
 * @param {Function} props.onFilterClose - Callback when filter overlay closes.
 * @param {Function} props.onResize - Callback for resize events.
 * @param {boolean} props.showDisclaimer - Whether to show the disclaimer.
 */
const Header = ({
  activeFilters = [],
  cardCount = 0,
  categories = {},
  categoryToDisplay = {},
  clearFilter: clearFilterProp,
  content = {},
  enableMultiselectFilters = true,
  filterDisplayNameByKey = {},
  filterOnChange = () => () => {},
  filterValues = {},
  isFilterActive = false,
  onFilterClose = () => {},
  onResize = () => {},
  showDisclaimer,
}) => {
  const [filterOverlayOpen, setFilterOverlayOpen] = useState(false);
  const [isSorM, setIsSorM] = useState(false);
  const [activeFilterKey, setActiveFilterKey] = useState(null);

  const {
    metadata: {
      description,
      shortDescription,
      disclaimerKey,
      disclaimerOverride,
    } = {},
    subType,
    type,
  } = content;

  const clearActiveAccordion = useCallback(() => {
    setActiveFilterKey(null);
  }, []);

  const openFilterOverlay = useCallback(() => {
    setFilterOverlayOpen(true);
    lockBodyScroll();
    $t('track', 'waffle_filters', { action: 'open', view: 'accordion' });
  }, []);

  const closeFilterOverlay = useCallback(() => {
    clearActiveAccordion();
    setFilterOverlayOpen(false);
    unlockBodyScroll();
    $t('track', 'waffle_filters', { action: 'done', view: 'accordion' });
  }, [clearActiveAccordion]);

  const clearFilter = useCallback(() => {
    clearFilterProp?.();
    $t('track', 'waffle_filters', {
      action: 'clear',
      view: isSorM ? 'accordion' : 'dropdown',
    });
  }, [clearFilterProp, isSorM]);

  const handleResize = useCallback(() => {
    const isMobile = Breakpoints.isSorM();
    onResize?.();
    setIsSorM(isMobile);

    if (isMobile) {
      setFilterOverlayOpen(false);
      unlockBodyScroll();
    }
  }, [onResize]);

  useEffect(() => {
    BTE.monitorResize();
    BTE.on('resize', handleResize, 'debounce');
    setIsSorM(Breakpoints.isSorM());

    return () => {
      BTE.remove('resize', handleResize, 'debounce');
      unlockBodyScroll();
    };
  }, []);

  useEffect(() => {
    const elementsToFindFlex = ['#hfs-header'];
    const elementsToFindBlock = ['.breaking-marquee'];

    elementsToFindFlex.forEach((item) => {
      const element = document.querySelector(item);
      if (element) element.style.display = filterOverlayOpen ? 'none' : 'flex';
    });

    elementsToFindBlock.forEach((item) => {
      const element = document.querySelector(item);
      if (element) element.style.display = filterOverlayOpen ? 'none' : 'block';
    });
  }, [filterOverlayOpen]);

  const filters = Object.keys(categories);
  const hasFilters = !!filters.length;
  const filtersHaveParams = filters.some((f) => !!categories[f].length);

  const results = (
    <span className={styles.resultsCount} data-testid="result-count" data-count={cardCount}>
      {cardCount.toLocaleString()}
      {' '}
      {i18next.t(cardCount === 1 ? 'Result' : 'Results')}
    </span>
  );

  const contentWithoutDisclaimer = {
    ...content,
    metadata: { ...(content.metadata || {}), disclaimerKey: null },
  };

  return (
    <div data-testid="waffleHeader" className={styles.waffleHeader}>
      <CollectionLead
        content={contentWithoutDisclaimer}
        dekRenderer={() => (
          <HeaderDek
            description={description}
            shortDescription={shortDescription}
            disclaimerKey={disclaimerKey}
            disclaimerOverride={disclaimerOverride}
            subType={subType}
            type={type}
            showDisclaimer={showDisclaimer}
          />
        )}
        disclaimerKey="NONE"
        pkgClassName={styles.waffleHeaderCollectionLead}
        postContentRenderer={() => (filtersHaveParams && hasFilters ? (
          <>
            <Filters
              activeFilters={activeFilters}
              categories={categories}
              categoryToDisplay={categoryToDisplay}
              clearFilter={clearFilter}
              enableMultiselectFilters={enableMultiselectFilters}
              filterDisplayNameByKey={filterDisplayNameByKey}
              filterOnChange={filterOnChange}
              filterValues={filterValues}
              content={content}
              onFilterClose={onFilterClose}
              filterOverlayOpen={filterOverlayOpen}
              activeFilterKey={activeFilterKey}
              isSorM={isSorM}
              closeFilterOverlay={closeFilterOverlay}
              clearActiveAccordion={clearActiveAccordion}
              setDesktopActiveAccordion={setActiveFilterKey}
            />
            <div className={styles.results}>
              {isFilterActive && (
                <FilterTagList
                  activeFilters={activeFilters}
                  clearFilter={clearFilter}
                  filterOnChange={filterOnChange}
                  enableMultiselectFilters={enableMultiselectFilters}
                  categoryToDisplay={categoryToDisplay}
                />
              )}
              {results}
            </div>
          </>
        ) : null)}
      />
      {filtersHaveParams && hasFilters && (
        <div className={styles.mobileFiltersCTA}>
          {/* Mobile Filters */}
          <button
            data-filter-action="open"
            className={styles.mobileCTABtn}
            onClick={openFilterOverlay}
            type="button"
            aria-expanded={filterOverlayOpen ? 'true' : 'false'}
          >
            {'Filter '}
            <span className="icon icon-angle-down" />
          </button>
          <div className={styles.results}>
            {isFilterActive && (
              <FilterTagList
                activeFilters={activeFilters}
                clearFilter={clearFilter}
                filterOnChange={filterOnChange}
                enableMultiselectFilters={enableMultiselectFilters}
                categoryToDisplay={categoryToDisplay}
              />
            )}
            {results}
          </div>
        </div>
      )}
    </div>
  );
};

export { Header };
